/**
 * PasswordRequirementList — the ONE password checklist, on the NIST SP 800-63B-4 policy (L-R7CLOSE
 * row 32; re-built by row 39 when the owner's 2026-09-26 ruling superseded R8-12).
 *
 * Renders the rows `evaluatePasswordRequirements(password, context)` answers — the SAME table every
 * server gates on — and ticks each from the policy's own judgement, naming each requirement by its
 * KEY through the `authShell` namespace. It restates no rule, so it cannot promise less than the
 * server enforces: the byte-ceiling row appears only while it fails because the policy says so, and
 * the person's own names and email (`context`) are judged exactly as the server judges them.
 *
 * It lives in the auth primitives because `<PasswordChangeCard>` (this package) renders it, and
 * login-shell — whose reset modal renders it too — sits ABOVE this package.
 *
 * The tick glyph is decorative; each item's state is announced as a word.
 *
 * @module @puffin-core/auth-shell/components/PasswordRequirementList
 */

import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { evaluatePasswordRequirements } from '@puffin-core/password-policy';

import { useAuthShellTranslate } from '../hooks/useAuthShellTranslate.js';

/**
 * @param {Object} props
 * @param {string} props.password - the candidate password, as typed
 * @param {{email?: string, firstName?: string, lastName?: string, words?: string[]}} props.context -
 *   what the host knows about the person (REQUIRED — `{}` when it knows nothing); their names and
 *   email do not count toward the length floor
 * @param {string} [props.testId='password-requirements']
 * @returns {React.JSX.Element}
 */
export function PasswordRequirementList({ password, context, testId = 'password-requirements' }) {
  const rows = useMemo(() => evaluatePasswordRequirements(password, context), [password, context]);
  const t = useAuthShellTranslate();
  return (
    <ul
      className="password-requirements"
      aria-label={t('authShell.password.requirements.label')}
      data-testid={testId}
    >
      {rows.map((row) => (
        <li
          key={row.id}
          className="password-requirement"
          data-requirement={row.id}
          data-met={row.met ? 'true' : 'false'}
        >
          <span aria-hidden="true">{row.met ? '✓' : '○'}</span>
          <span>
            {t(`authShell.password.requirement.${row.id}`, {
              min: row.minLength,
              max: row.maxBytes,
            })}
          </span>
          <span className="visually-hidden">
            {t(
              row.met
                ? 'authShell.password.requirement.met'
                : 'authShell.password.requirement.notMet',
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

PasswordRequirementList.propTypes = {
  password: PropTypes.string.isRequired,
  context: PropTypes.shape({
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    words: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  testId: PropTypes.string,
};

export default PasswordRequirementList;
